<template>
  <div
    id="app"
    :class="[
      isGameView ? 'resize-page' : '',
      isLanding ? 'landing' : '',
      $route.name ? 'site-' + $route.name : '',
      ((isSpecialPage && !$store.getters['auth/isLoggedIn']) ||
        isManagementPage) &&
      smallWindow
        ? 'mobile-view'
        : '',
      $store.state.auth.acpMode ? 'acp-border position-relative' : '',
    ]"
  >
    <span
      v-if="$store.state.auth.acpMode"
      id="acp-warning"
      class="text-danger bold position-sticky"
    >
      ACHTUNG! ACP-Modus! Bitte achtsam verhalten und unbedingt wieder
      ausloggen!
    </span>
    <fall-back-page v-if="showFallBackPage"></fall-back-page
    ><template v-else>
      <MainContent />

      <broadcasting
        @socketStatus="handleSocketStatus"
        @logout="handleLogout"
        @connectionLost="handleConnectionLost"
        @reconnected="handleReconnected"
        @tabResumed="handleTabResumed"
        @stateChange="handleConnectionStateChange"
      />
      <connection-indicator v-if="isLoggedIn" :state="connectionState" />
      <preloader v-if="preloadContent"></preloader>
      <pipeline v-if="isLoggedIn"></pipeline>
      <global-popups v-if="isLoggedIn"></global-popups>

      <!-- Global Offer Modal (v2 API) - Highest Priority -->
      <modal-offer-dynamic
        v-if="
          isLoggedIn &&
          useV2Offers &&
          globalOfferData &&
          showGlobalOfferModal &&
          !showOfferChainModal &&
          (offerManualOpen ||
            (pipelineFinished && !rewardPopupRunning && !holdPipelineForChain))
        "
        :offer-data="globalOfferData"
        @hidden="onGlobalOfferHidden"
      />

      <!-- Offer Chain Modal (v2 API) - badge-opened, not pipeline-gated -->
      <modal-offer-chain
        v-if="
          isLoggedIn &&
          offerChainEnabled &&
          offerChainData &&
          showOfferChainModal
        "
        :offer-data="offerChainData"
        @hidden="onOfferChainHidden"
      />

      <CookieModal />
    </template>
  </div>
</template>

<script>
import broadcasting from "./broadcasting.vue";
import preloader from "./components/preloader/preloader";
import * as tournamentAPI from "./API/tournament.js";
import AnimationParsingFunctions from "@/components/animation/animation-parsing-functions";
import AnimationFullAccomplishment from "./components/animation/animation-full-accomplishment";
import offerModal from "@/mixins/offerModal.js";
import loadScripts from "@/mixins/loadScripts";
import AnimationKingWin from "@/components/animation/animation-king-win";
import Pipeline from "@/components/pipeline/pipeline";
import GlobalPopups from "@/components/global-popups";
import FallBackPage from "./views/FallBackPage.vue";
import wsChannels from "@/mixins/wsChannels.js";
import ModalOfferDynamic from "@/components/shop/offers/modal-offer-dynamic.vue";
import ModalOfferChain from "@/components/shop/offers/chain/modal-offer-chain.vue";
import ConnectionIndicator from "@/components/base/connection-indicator.vue";
import CookieModal from "@/components/legal/cookie-modal.vue";
import { fetchOfferByTrigger, OFFER_TRIGGERS } from "@/API/offers";
export default {
  name: "App",
  components: {
    GlobalPopups,
    Pipeline,
    preloader,
    broadcasting,
    ModalOfferDynamic,
    ModalOfferChain,
    ConnectionIndicator,
    CookieModal,
    MainContent: () =>
      import(/* webpackChunkName: "main" */ "@/views/MainContent.vue"),
    FallBackPage,
  },
  mixins: [
    AnimationParsingFunctions,
    AnimationFullAccomplishment,
    offerModal,
    loadScripts,
    AnimationKingWin,
    wsChannels,
  ],
  data() {
    return {
      socket: null,
      subscribedChannels: new Set(),
      currentTournamentChannelId: null,
      currentBattleChannelId: null,
      wasConnected: false,
      connectionState: "disconnected",
      offerRefreshTimeout: null,
      animationBufferDrainOwner: true,
      // After a chain purchase, defer the generic lobby-offer refresh until the
      // user closes the (reopened) chain modal — set on purchase, consumed on
      // chain close.
      deferLobbyRefresh: false,
    };
  },
  computed: {
    isLoggedIn: function () {
      return this.$store.getters["auth/isLoggedIn"];
    },
    useV2Offers() {
      return !!this.$whitelabel?.featuresEnabled?.v2Offers;
    },
    currentUserId() {
      return this.$store.getters["user/currentUser"].id;
    },

    isConnected: function () {
      return (
        this.socket &&
        this.socket.connector.pusher.connection.connection !== null
      );
    },

    isManagementPage() {
      return (
        this.$route.matched[0] && this.$route.matched[0].path === "/management"
      );
    },
    isSpecialPage() {
      return (
        this.$route.path === "/imprint" ||
        this.$route.path === "/privacy" ||
        this.$route.path === "/tac" ||
        this.$route.path === "/raffle_tac"
      );
    },

    isGameView() {
      return (
        this.$route.name === "game" ||
        this.$route.name === "kingspath-game" ||
        this.$route.name === "tournament-game"
      );
    },
    isLanding() {
      return (
        (!this.isLoggedIn && this.$route.path === "/") ||
        this.$route.path === "/login" ||
        this.isManagementPage
      );
    },
    preloadContent() {
      return this.isLoggedIn && !this.isSpecialPage && !this.isManagementPage;
    },
    teamMember() {
      return this.$store.getters["teams/isTeamMember"];
    },
    championsBattleActive() {
      return this.$store.getters["champions/getCurrentBattle"] !== null;
    },

    musicPreference() {
      return this.$store.getters["sound/getMusicPreference"];
    },

    globalOfferData() {
      return this.$store.getters["shop/getGlobalOfferData"];
    },

    showGlobalOfferModal() {
      return this.$store.getters["shop/getShowGlobalOfferModal"];
    },
    // True when reopened from the menu-event badge — lets the modal bypass the
    // auto-show pipeline gates so a user-initiated reopen always renders.
    offerManualOpen() {
      return this.$store.getters["shop/getOfferManualOpen"];
    },
    offerChainEnabled() {
      return !!this.$whitelabel?.featuresEnabled?.offerChain;
    },
    offerChainData() {
      return this.$store.getters["shop/getOfferChainData"];
    },
    showOfferChainModal() {
      return this.$store.getters["shop/getShowOfferChainModal"];
    },
    // True while a chain purchase is reopening its modal — the generic offer must
    // stay suppressed until the user closes the chain.
    holdPipelineForChain() {
      return this.$store.getters["popups/getHoldPipelineForChain"];
    },
    smallWindow() {
      return this.$store.state.windowWidth < 1024;
    },
    showFallBackPage() {
      if (this.isLoggedIn) {
        return this.smallWindow;
      } else {
        return (
          this.smallWindow && !this.isSpecialPage && !this.isManagementPage
        );
      }
    },
    tournamentActive() {
      return this.$store.state.tournaments.currentTournamentId !== null;
    },
    pipelineFinished() {
      return this.$store.getters["popups/getPipelineFinished"];
    },
    rewardPopupRunning() {
      return this.$store.getters["popups/getQueueAnimationIsRunning"];
    },
  },
  watch: {
    musicPreference() {
      if (this.musicPreference) {
        this.playBackgroundMusic(true);
      } else {
        this.stopBackgroundMusic();
      }
    },
    teamMember: {
      handler() {
        if (this.teamMember) {
          this.$store.dispatch("teams/getUserTeamInfo").then(() => {
            if (!this.$store.getters["teams/teamActive"]) {
              this.getTeamNotifications();
            }
          });
        }
      },
    },
    tournamentActive: {
      handler(active) {
        if (active) {
          let id = this.$store.state.tournaments.currentTournamentId;
          this.getTournamentUpdates(id);
        } else if (this.socket && this.currentTournamentChannelId) {
          // Tournament ended — leave the channel
          this.socket.leave(`tournament.${this.currentTournamentChannelId}`);
          this.subscribedChannels.delete(
            `tournament.${this.currentTournamentChannelId}`
          );
          this.currentTournamentChannelId = null;
        }
      },
    },
    championsBattleActive: {
      handler(active) {
        if (active) {
          this.getChampionsBattleNotifications();
        } else if (this.socket && this.currentBattleChannelId) {
          // Battle ended — leave the channel
          this.socket.leave(`user.battle.${this.currentBattleChannelId}`);
          this.subscribedChannels.delete(
            `presence-user.battle.${this.currentBattleChannelId}`
          );
          this.currentBattleChannelId = null;
        }
      },
    },

    isLoggedIn: {
      handler(data) {
        if (data) {
          this.loadScriptsExternal();
        }
      },
    },

    $route(to) {
      this.playBackgroundMusic(true);
      this.firebaseSetScreen(to.path, "default");
    },
  },
  created() {
    this.clearStalePopupState();
    // Transient flag must not survive a refresh (whole state is persisted).
    this.$store.commit("shop/setShowOfferChainModal", false);
    this.$eventBus.$on("languageChanged", () => {
      this.$forceUpdate();
    });
    this.$root.$on(
      "v2-offer-purchase-complete",
      this.refreshLobbyOfferAfterPipeline
    );
    this.$root.$on("offer-purchase-reward", this.onOfferPurchaseReward);
    window.addEventListener("resize", this.onResize);
    this.onResize();
    this.$store.commit("popups/setDocumentHidden", document.hidden);
    document.addEventListener("visibilitychange", this.onVisibilityChange);
  },

  mounted() {
    this.$store.dispatch("resetStore");

    // this.getMobileOperatingSystem(); //TODO: Use deeplink from App
  },
  beforeDestroy() {
    this.$root.$off(
      "v2-offer-purchase-complete",
      this.refreshLobbyOfferAfterPipeline
    );
    this.$root.$off("offer-purchase-reward", this.onOfferPurchaseReward);
    clearTimeout(this.offerRefreshTimeout);
    window.removeEventListener("resize", this.onResize);
    document.removeEventListener("visibilitychange", this.onVisibilityChange);
  },

  methods: {
    onResize() {
      this.$store.commit("updateWindowWidth", window.innerWidth);
    },
    onVisibilityChange() {
      this.$store.commit("popups/setDocumentHidden", document.hidden);
      this.drainBufferedAnimationEvents();
    },
    clearStalePopupState() {
      this.$store.commit("popups/setPipelinePaused", false);

      if (!this.$store.getters["shop/getShowBuyResult"]) {
        this.$store.commit("shop/clearLastPayment");
      }
    },
    refreshLobbyOfferAfterPipeline() {
      clearTimeout(this.offerRefreshTimeout);
      this.offerRefreshTimeout = setTimeout(() => {
        this.refreshLobbyOffer();
      }, 800);
    },
    async refreshLobbyOffer() {
      if (!this.isLoggedIn || !this.useV2Offers) {
        return;
      }

      this.$store.commit("shop/clearGlobalOfferData");

      if (!this.popupsUnlocked) {
        return;
      }

      // Skip while a chain purchase is reopening its modal (or the chain modal
      // is open) — the generic offer must not pop under the chain. It resumes
      // once the user closes the chain.
      if (this.holdPipelineForChain || this.showOfferChainModal) {
        return;
      }

      // Skip when season-pass modal is open — don't stack offers over it.
      if (document.querySelector("#modal-season-pass.show")) {
        return;
      }

      try {
        const data = await fetchOfferByTrigger(OFFER_TRIGGERS.LOBBY);

        if (data && data.offer) {
          this.$store.commit("shop/setGlobalOfferData", data.offer);
          this.$store.commit("shop/setShowGlobalOfferModal", true);
        }
      } catch (error) {
        console.error("Error refreshing lobby offer:", error);
      }
    },
    handleSocketStatus(payload) {
      if (payload) {
        if (payload.connector.pusher.connection.connection) {
          this.socket = payload;
          // Leave existing channels before re-subscribing to prevent duplicate listeners
          this.unsubscribeAll();
          this.subscribeToSocketChannels();

          if (this.teamMember) {
            this.$store.dispatch("teams/getUserTeamInfo").then((res) => {
              if (res.data.data.team.status === "ACTIVE") {
                this.setUpChat();
              }
            });
          }
          if (this.championsBattleActive) {
            this.getChampionsBattleNotifications();
          }
          if (this.tournamentActive) {
            this.getTournamentUpdates(
              this.$store.state.tournaments.currentTournamentId
            );
          }
          this.wasConnected = true;
        }
      }
    },

    handleLogout() {
      this.unsubscribeAll();
      this.wasConnected = false;
    },

    handleConnectionLost() {
      // Connection lost — indicator dot handles the visual feedback
    },

    handleReconnected() {
      // Connection restored after a drop — refresh critical state that may have been missed
      console.log("[App] : Reconnected — refreshing stale data");
      this.refreshStaleData();
    },

    handleConnectionStateChange(state) {
      this.connectionState = state;
    },

    handleTabResumed() {
      // Tab became visible while still connected — refresh data that may be stale
      this.refreshStaleData();
    },

    refreshStaleData() {
      // Refresh wallet and user data that may have changed while disconnected/in background
      this.$store.dispatch("user/updateUser");
    },

    subscribeToSocketChannels() {
      if (this.socket) {
        const userId = this.$store.getters["user/currentUser"].id;
        const privateSocket = this.socket.private(`user.${userId}`);
        const publicSocket = this.socket.channel("system");

        privateSocket.subscription.bind("pusher:subscription_succeeded", () => {
          console.log("[App] : Private channel subscription succeeded");
        });

        privateSocket.subscription.bind(
          "pusher:subscription_error",
          (error) => {
            console.error("[App] : Private channel subscription error:", error);
          }
        );

        Object.keys(this.privateActions).forEach((messageId) => {
          const handler = this.privateActions[messageId];
          privateSocket.listen(messageId, handler);
        });
        Object.keys(this.publicActions).forEach((messageId) => {
          const handler = this.publicActions[messageId];
          publicSocket.listen(messageId, handler);
        });

        this.subscribedChannels.add(`private-user.${userId}`);
        this.subscribedChannels.add("system");
      }
    },

    unsubscribeAll() {
      if (!this.socket || !this.socket.connector) {
        return;
      }
      let channels = Object.keys(this.socket.connector.channels);
      channels.forEach((channel) => {
        this.socket.leave(channel);
      });
      this.subscribedChannels.clear();
      this.currentTournamentChannelId = null;
      this.currentBattleChannelId = null;
    },

    getTournamentUpdates(id) {
      if (!this.socket) return;
      // Leave previous tournament channel if different
      if (
        this.currentTournamentChannelId &&
        this.currentTournamentChannelId !== id
      ) {
        this.socket.leave(`tournament.${this.currentTournamentChannelId}`);
        this.subscribedChannels.delete(
          `tournament.${this.currentTournamentChannelId}`
        );
      }
      this.currentTournamentChannelId = id;
      // Use Echo instead of direct Pusher to keep channel tracking consistent
      this.socket
        .channel(`tournament.${id}`)
        .listen(".tournament.update", (e) => {
          this.$store.commit("tournaments/setTournamentCurrentUpdateData", e);
        });
      this.subscribedChannels.add(`tournament.${id}`);
    },

    setUpChat() {
      if (!this.socket) return;
      if (!this.$store.state.chat.chatHistoryFetched) {
        this.$store.dispatch("chat/fetchChatHistory");
      }
      const teamId = this.$store.state.teams.userTeam.id;
      this.socket
        .join(`team.${teamId}`)
        .here((users) =>
          this.$store.commit("chat/setListOfOnlineTeamMembers", users)
        )
        .joining((user) => {
          this.$store.commit("chat/addTeamMemberToList", user);
        })
        .leaving((user) =>
          this.$store.commit("chat/removeTeamMemberFromList", user)
        )
        .listen(".team.chat", (message) => {
          this.$store.commit("chat/addMessageToChat", {
            message: message,
            route: this.$route.path,
          });
        })
        .listen(".team.chat.user_card_request", (request) => {
          this.$store.commit("chat/addMessageToChat", {
            message: request,
            route: this.$route.path,
          });
        });
      this.subscribedChannels.add(`presence-team.${teamId}`);
    },

    getTeamNotifications() {
      if (!this.socket) return;
      const teamId = this.$store.getters["user/currentUser"].team_id;
      this.socket.join(`team.${teamId}`).listen(".team.active", () => {
        console.log("team active!");
        this.$store.commit("popups/setAlertBannerContent", {
          alertHeading: this.$t("teams.now_active.title"),
          alertText: this.$t("teams.now_active.message"),
          type: "friend",
          variant: "success",
        });
      });
      this.subscribedChannels.add(`presence-team.${teamId}`);
    },

    getChampionsBattleNotifications() {
      if (!this.socket) return;
      const battle = this.$store.getters["champions/getCurrentBattle"];
      if (!battle) return;
      const battleId = battle.battle_id;

      // Leave previous battle channel if different
      if (
        this.currentBattleChannelId &&
        this.currentBattleChannelId !== battleId
      ) {
        this.socket.leave(`user.battle.${this.currentBattleChannelId}`);
        this.subscribedChannels.delete(
          `presence-user.battle.${this.currentBattleChannelId}`
        );
      }
      this.currentBattleChannelId = battleId;

      this.socket
        .join(`user.battle.${battleId}`)
        .listen(".user.battle.update", (e) => {
          let currentBattle = this.$store.getters["champions/getCurrentBattle"];
          if (currentBattle && currentBattle.battle_id !== e.battle) {
            return;
          }

          if (this.currentUserId === e.user_id) {
            const gold = e.gold;
            const user_id = e.user_id;
            const ended_at = e.ended_at;
            currentBattle.gold = gold;
            currentBattle.user_id = user_id;
            currentBattle.ended_at = ended_at;

            this.$store.commit("champions/setCurrentBattle", currentBattle);
          } else {
            this.$store.commit("champions/setCurrentOpponentId", null);
            this.$store.commit("champions/setCurrentOpponentId", e.user_id);
          }
        });
      this.subscribedChannels.add(`presence-user.battle.${battleId}`);
    },

    fetchTournamentHistoryData: async function (win_data) {
      await tournamentAPI.fetchHistoryTournament().then((res) => {
        const tournament_id = win_data.tournament_history_id;
        let tournaments = res.list;
        let result;
        for (let i = 0; i < tournaments.length; i++) {
          if (tournaments[i].tournament_history_id === tournament_id) {
            result = tournaments[i];
            result["gold_amount"] = win_data["gold_amount"];
            result["crown_amount"] = win_data["crown_amount"];
            result["loyalty_amount"] = win_data["loyalty_amount"];
            break;
          }
        }

        this.$store
          .dispatch("tournaments/updateTournamentWin", result)
          .then(() => {
            this.$bvModal.show("modal-tournament-win" + result.id);
          });
      });
    },

    onGlobalOfferHidden() {
      if (this.offerManualOpen) {
        this.$store.commit("shop/setShowGlobalOfferModal", false);
        this.$store.commit("shop/setOfferManualOpen", false);
        return;
      }
      // Only reset the show flag when the modal was closed by the user.
      // If the v-if destroyed it due to pipelineFinished/rewardPopupRunning
      // flipping, leave the flag true so it remounts once gates clear.
      if (this.pipelineFinished && !this.rewardPopupRunning) {
        this.$store.commit("shop/setShowGlobalOfferModal", false);
      }
    },
    onOfferChainHidden() {
      // Hide the modal but keep chain data so the badge stays visible.
      this.$store.commit("shop/setShowOfferChainModal", false);
      // Deferred generic lobby-offer refresh after a chain purchase: now that
      // the user closed the chain, run it (debounced; the hold flag is cleared
      // by the pipeline's @hidden handler, so the guard in refreshLobbyOffer
      // passes by the time the timer fires).
      if (this.deferLobbyRefresh) {
        this.deferLobbyRefresh = false;
        this.refreshLobbyOfferAfterPipeline();
      }
    },
    // After any v2 offer purchase (buy-complete dismissed): play the full
    // reward-earned animation. For a chain, reopen it AFTER the animation
    // finishes so its advance transition doesn't play under the animation.
    // Works for both inline and redirect-reload payment paths.
    onOfferPurchaseReward(payload) {
      const rewards = (payload && payload.rewards) || [];
      const isChain = !!(payload && payload.isChain);
      const offerId = payload && payload.offerId;

      if (rewards.length) {
        this.AnimationFullRewardEarnedAnimate(rewards, false);
      }
      if (!isChain || !this.offerChainEnabled) return;

      // Chain purchase: hold the generic lobby-offer refresh until the chain
      // modal is closed by the user (deferred, not dropped).
      this.deferLobbyRefresh = true;

      const reopenChain = () => {
        this.$store.commit("shop/setOfferChainUnlockAnim", true);
        // Refetch all chains; the action refreshes the selected chain by id.
        this.$store.dispatch("shop/fetchOfferChain").then((active) => {
          const list = Array.isArray(active) ? active : [];
          // The selected chain may be stale (its last package was just bought).
          // Resolve the chain to reopen from the active list only.
          let chain = this.$store.getters["shop/getOfferChainData"];
          let stillActive = chain && list.some((c) => c && c.id === chain.id);
          // Redirect-reload path: in-memory selection was lost — pick the
          // purchased chain by offer id from the active list.
          if (!stillActive && offerId != null) {
            chain =
              list.find(
                (c) =>
                  c &&
                  (c.offer_id === offerId ||
                    (c.offer && c.offer.id === offerId))
              ) || null;
            stillActive = !!chain;
            if (chain) this.$store.commit("shop/setOfferChainData", chain);
          }
          if (stillActive) {
            this.$store.commit("shop/setShowOfferChainModal", true);
            this.$nextTick(() => {
              this.$bvModal.show("modal-offer-chain");
            });
          } else {
            this.$store.commit("shop/setOfferChainUnlockAnim", false);
            // No chain modal will reopen → release the pipeline hold and let it
            // continue (nudge the queue; the watcher advances when not paused).
            if (this.$store.getters["popups/getHoldPipelineForChain"]) {
              this.$store.commit("popups/setHoldPipelineForChain", false);
              this.$store.commit("popups/setPipelinePaused", false);
              this.$store.commit("popups/setNextPipelineQueueStep");
            }
            // Chain is done (no reopen) → run the deferred lobby-offer refresh.
            if (this.deferLobbyRefresh) {
              this.deferLobbyRefresh = false;
              this.refreshLobbyOfferAfterPipeline();
            }
          }
        });
      };

      if (rewards.length) this.afterQueueAnimation(reopenChain);
      else reopenChain();
    },
    // Run `cb` once the reward-animation queue has gone running→idle. A timeout
    // fallback covers the case where the animation never starts.
    afterQueueAnimation(cb) {
      const getRunning = () =>
        this.$store.getters["popups/getQueueAnimationIsRunning"];
      let started = getRunning();
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        unwatch();
        clearTimeout(fallback);
        cb();
      };
      const unwatch = this.$store.watch(getRunning, (running) => {
        if (running) started = true;
        else if (started) finish();
      });
      const fallback = setTimeout(() => {
        if (!started) finish();
      }, 1500);
    },
  },
};
</script>
<style lang="scss">
.acp-border {
  border: 5px solid red;
  #acp-warning {
    top: 70px;
    z-index: 5000;
  }
}
</style>
